import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAdminTags } from "./fetchAdminTags";

export const ADMIN_TAGS_QUERY_KEY = ["casinoConfiguration", "adminTags"] as const;

export function useAdminTags() {
  return useQuery({
    queryKey: ADMIN_TAGS_QUERY_KEY,
    queryFn: fetchAdminTags,
    staleTime: 5 * 60 * 1000,
  });
}

export type CreateAdminTagPayload = {
  /** URL-safe slug, immutable once created. Validated by the form. */
  tag_id: string;
  name: string;
  /** 6-digit hex including the leading `#`. */
  color: string;
};

export type UpdateAdminTagPayload = {
  /** URL key — never sent in the body, used to build the PATCH path. */
  tag_id: string;
  name: string;
  /**
   * Optional on update — when omitted, the existing color stays untouched
   * on the backend. The form treats it as untouched if the user opened the
   * modal, didn't interact with the color input, and the tag was colorless
   * to begin with (avoids silently writing the placeholder grey).
   */
  color?: string;
};

/** Creates a brand-new tag definition; invalidates the catalog on success. */
export function useCreateAdminTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateAdminTagPayload) => {
      // Lazy-required inside `mutationFn` so the network module isn't pulled
      // in by every consumer of `useAdminTags`.
      const { createAdminTag } = await import("./saveAdminTag");
      return createAdminTag(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_TAGS_QUERY_KEY });
    },
  });
}

/** Updates an existing tag's name/color (tag_id is the URL key). */
export function useUpdateAdminTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateAdminTagPayload) => {
      const { updateAdminTag } = await import("./saveAdminTag");
      return updateAdminTag(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_TAGS_QUERY_KEY });
    },
  });
}
